import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import queries from "../../queries";
import {FormattedMessage} from "react-intl";

import '../../style/ProjectCreation.scss';
import BaseProjectSettings from "./BaseProjectSettings";
import {apolloClient} from "../../config";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PROJECT_TEMPLATES, applyTemplate } from '../../projectTemplates';
import { showToast } from '../../toast';
import { t } from '../../i18n';

class ProjectCreation extends Component {

  state = {
    templateId: 'showcase',
    creating: false,
    fontRequiredError: false,
    buttonBackgroundRequiredError: false,
    project: {
      name: '',
      languages: [],
      color_palette: ['#000000'],
      fonts: [],
      // Valeurs par défaut du style des boutons : l'utilisateur n'a plus à tout renseigner pour pouvoir créer.
      default_button_background_type: 'color',
      default_button_background_color: '#000000',
      default_button_text_color: '#ffffff',
      default_button_shape: 'rounded_rectangle',
      default_button_width_percentage: 50,
      default_button_opacity: 100,
      default_button_text_size: 1,
    },
    nameRequiredError: false,
  };

  render() {
    const {project, templateId, creating, nameRequiredError, fontRequiredError, buttonBackgroundRequiredError, languageRequiredError, languageSelectorError} = this.state;

    document.fonts.clear();
    for (const font of project.fonts) {
      const fontFace = new FontFace(font.name, `url(${font.uri})`);
      fontFace.load().then((loadedFont) => {
        document.fonts.add(loadedFont);
      }).catch(function(error) {
        // error occurred
      });
    }

    const createProject = async () => {
      const errorState = {
        nameRequiredError: !project.name || !project.name.trim(),
        fontRequiredError: project.fonts.length === 0,
        buttonBackgroundRequiredError: !project.default_button_background_type,
        languageRequiredError: project.languages.length === 0,
        languageSelectorError: (!!project.languages && project.languages.length > 1) && (!project.language_selector_background_color || !project.language_selector_border_color  || ! project.language_selector_text_color),
      };
      this.setState(errorState);
      if(errorState.nameRequiredError || errorState.fontRequiredError || errorState.buttonBackgroundRequiredError || errorState.languageRequiredError || errorState.languageSelectorError) {
        return;
      }
      this.setState({ creating: true });
      const projectData = {
        ...project,
        fonts: undefined,
        languages: undefined,
        language_codes: project.languages.map((lang) => lang.code),
        primary_language_code: project.primary_language_code || project.languages[0].code,
        default_button_fit_whole_cell: false, //TODO
      };

      const response = await apolloClient.mutate({
        mutation: queries.projects.create,
        variables: {input: projectData},
        refetchQueries: () => [{ query: queries.projects.list }]
      });

      const createdProject = response.data.createProject;

      await Promise.all(project.fonts.map((font) => (
        apolloClient.mutate({
          mutation: queries.fonts.create,
          variables: {input: {
              name: font.name,
              uri: font.uri,
              project_id: createdProject.id
            }},
          refetchQueries: () => [{ query: queries.projects.getWithChildren, variables: {id: createdProject.id} }]
        })
      )));

      try {
        await applyTemplate(createdProject.id, PROJECT_TEMPLATES.find((tpl) => tpl.id === templateId));
        showToast(t('ui.create.created'));
      } catch (e) {
        console.error(e);
        showToast(t('ui.create.template_fail'), 'error', 6000);
      }
      this.props.history.push(`/project/${createdProject.id}`);
    };

    return (
      <div className={'tsk-project-creation-form'}>
        <header className='tsk-creation-hero'>
          <h1>{t('ui.create.title')}</h1>
          <p>{t('ui.create.lead')}</p>
        </header>
        <div className={'tsk-form-content'}>
          <section className='tsk-creation-card'>
          <h2 className='tsk-creation-step'><span>1</span> {t('ui.create.step1')}</h2>
          <div className='tsk-template-grid' role='radiogroup' aria-label={t('ui.create.template_aria')}>
            {PROJECT_TEMPLATES.map((tpl) => (
              <button
                key={tpl.id}
                type='button'
                role='radio'
                aria-checked={templateId === tpl.id}
                className={`tsk-template-card${templateId === tpl.id ? ' is-selected' : ''}`}
                onClick={() => this.setState({ templateId: tpl.id })}
              >
                <span className='tsk-template-card__icon'><FontAwesomeIcon icon={tpl.icon.includes('image') || tpl.icon === 'file' ? ['fas', tpl.icon] : tpl.icon} /></span>
                <strong>{tpl.title}</strong>
                <small>{tpl.description}</small>
              </button>
            ))}
          </div>
          </section>
          <section className='tsk-creation-card'>
          <h2 className='tsk-creation-step'><span>2</span> {t('ui.create.step2')}</h2>
          <BaseProjectSettings
            project={project}
            onNameChange={(name) => {
              this.setState({project: {...project, name}})
            }}
            onNewColor={() => {
              project.color_palette.push('#000000');
              this.setState({project});
            }}
            onLanguageSelectorSettingsUpdate={(input) => {
              const {language_selector_background_color, language_selector_border_color, language_selector_text_color, language_selector_shape, language_selector_display_type} = input;
              if(language_selector_background_color)
                project.language_selector_background_color = language_selector_background_color;
              if(language_selector_border_color)
                project.language_selector_border_color = language_selector_border_color;
              if(language_selector_text_color)
                project.language_selector_text_color = language_selector_text_color;
              if(language_selector_shape)
                project.language_selector_shape = language_selector_shape;
              if(language_selector_display_type)
                project.language_selector_display_type = language_selector_display_type;
              this.setState({project});
            }}
            onLanguagesUpdate={(languages) => {
              project.languages = languages;
              this.setState({project});
            }}
            onNewPrimaryLanguage={(languageCode) => {
              project.primary_language_code = languageCode;
              this.setState({project});
            }}
            onColorUpdate={(color, idx) => {
              project.color_palette[idx] = color;
              this.setState({project});
            }}
            onNewFont={async ({name, uri}) => {
              project.fonts.push({ name, uri });
              this.setState({project, fontRequiredError: false});
            }}
            onNewButtonsSettings={(btnSettings) => {
              console.log('button settings changed: ', project);
              this.setState({
                project: {
                  ...project,
                  ...(btnSettings.textColor !== undefined ? {default_button_text_color: btnSettings.textColor}: {}),
                  ...(btnSettings.textSize !== undefined ? {default_button_text_size: btnSettings.textSize}: {}),
                  ...(btnSettings.font !== undefined ? {default_button_font: btnSettings.font}: {}),
                  ...(btnSettings.opacity !== undefined ? {default_button_opacity: btnSettings.opacity}: {}),
                  ...(btnSettings.shape !== undefined ? {default_button_shape: btnSettings.shape}: {}),
                  ...(btnSettings.widthPercentage !== undefined ? {default_button_width_percentage: btnSettings.widthPercentage}: {}),
                  ...(btnSettings.buttonBackgroundType !== undefined ? {default_button_background_type: btnSettings.buttonBackgroundType}: {}),
                  ...(btnSettings.buttonBackgroundImageUri !== undefined ? {default_button_background_image_uri: btnSettings.buttonBackgroundImageUri}: {}),
                  ...(btnSettings.buttonBackgroundColor !== undefined ? {default_button_background_color: btnSettings.buttonBackgroundColor}: {}),
                }
              });
            }}
          />
          </section>
          <div className={'tsk-error'} style={{display: nameRequiredError? 'block': 'none'}}>
            {t('ui.create.name_error')}
          </div>
          <div className={'tsk-error'} style={{display: fontRequiredError? 'block': 'none'}}>
            <FormattedMessage id={'project.creation.font.required.error'} />
          </div>
          <div className={'tsk-error'} style={{display: buttonBackgroundRequiredError? 'block': 'none'}}>
            <FormattedMessage id={'project.creation.buttonbackground.required.error'} />
          </div>
          <div className={'tsk-error'} style={{display: languageRequiredError? 'block': 'none'}}>
            <FormattedMessage id={'project.creation.language.required.error'} />
          </div>
          <div className={'tsk-error'} style={{display: languageSelectorError? 'block': 'none'}}>
            <FormattedMessage id={'project.creation.languageselector.required.error'} />
          </div>
        </div>
        <div className={'tsk-form-bottom'}>
          <ul className='tsk-creation-checks'>
            <li className={project.name && project.name.trim() ? 'is-ok' : ''}>{t('ui.create.check_name')}</li>
            <li className={project.languages.length > 0 ? 'is-ok' : ''}>{t('ui.create.check_lang')}</li>
            <li className={project.fonts.length > 0 ? 'is-ok' : ''}>{t('ui.create.check_font')}</li>
          </ul>
          <button className={'tsk-button'} type="button" disabled={creating} onClick={createProject}>
            {creating ? t('ui.create.creating') : t('ui.create.submit')}
          </button>
        </div>
      </div>
    )
  }
}

export default withRouter(ProjectCreation);
